import { useNavigate } from 'react-router-dom';
import { Sparkles, Phone, MapPin, Clock, ChevronRight, ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { CONTACT_LINE } from '../../data/business';

/* ============================================================
   Composants présentation des pages de service.
   Réutilisent strictement les classes / tokens du design system
   (couleur #0052FF, dégradés blue→indigo→purple, Outfit/Jakarta).
   ============================================================ */

/* Bouton "Devis gratuit" : si la page embarque le widget (#devis),
   on scrolle dessus ; sinon on renvoie vers le widget de la home. */
export const useGoQuote = (hasWidget) => {
  const navigate = useNavigate();
  return () => {
    if (hasWidget && typeof document !== 'undefined') {
      const el = document.getElementById('devis');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    try { sessionStorage.setItem('allorep:scrollTo', '#devis'); } catch {}
    navigate('/');
  };
};

export const ServiceHero = ({ eyebrow = 'Réparation à Montpellier', h1, intro, hasWidget }) => {
  const goQuote = useGoQuote(hasWidget);
  return (
    <section className="relative overflow-hidden gradient-section-blue">
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-400/15 to-purple-400/15 rounded-full blur-3xl" />
      <div className="container-custom relative z-10 py-12 lg:py-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full mb-6">
            <Sparkles size={16} className="text-blue-500" />
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              {eyebrow}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5">
            {h1}
          </h1>

          <p className="text-lg text-slate-600 leading-relaxed mb-6">{intro}</p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button
              onClick={goQuote}
              className="group relative overflow-hidden px-6 py-3.5 rounded-full font-semibold text-sm text-white transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 hover:-translate-y-0.5"
              data-testid="service-cta-devis"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600" />
              <span className="relative flex items-center justify-center gap-2">
                <Sparkles size={16} />
                Devis gratuit
                <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
              </span>
            </button>
            <a
              href="tel:+33983820754"
              className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-all"
              data-testid="service-cta-phone"
            >
              <Phone size={16} className="text-green-500" />
              09 83 82 07 54
            </a>
          </div>

          {/* Coordonnées en clair (NAP + horaires) */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={15} className="text-blue-500" />
              7 place des Martyrs de la Résistance, 34000 Montpellier
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={15} className="text-amber-500" />
              Lun–Sam 10h–20h, sans rendez-vous
            </span>
          </div>
          <p className="sr-only">{CONTACT_LINE}</p>
        </div>
      </div>
    </section>
  );
};

/* Section de contenu standard : H2 + corps */
export const ServiceSection = ({ title, children, className = '' }) => (
  <section className={`container-custom py-8 lg:py-10 ${className}`}>
    <div className="max-w-3xl">
      {title && (
        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-5">{title}</h2>
      )}
      <div className="space-y-4 text-slate-600 leading-relaxed text-[15px] lg:text-base service-prose">
        {children}
      </div>
    </div>
  </section>
);

/* Liste à puces stylée */
export const ServiceList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="mt-2 flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/* Tableau de prix — réutilise les tokens du site.
   La mention "le devis final dépend de l'état de l'appareil" est obligatoire. */
export const PriceTable = ({ columns, rows, note }) => (
  <div className="container-custom py-2">
    <div className="max-w-3xl">
      <div className="overflow-x-auto rounded-2xl border border-slate-100 shadow-sm">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              {columns.map((c, i) => (
                <th key={i} className="px-4 py-3 font-semibold whitespace-nowrap">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, ri) => (
              <tr key={ri} className="bg-white hover:bg-slate-50/70 transition-colors">
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-4 py-3 ${ci === 0 ? 'font-medium text-slate-800' : 'text-slate-600'} ${
                      ci === row.length - 1 ? 'font-semibold text-blue-600 whitespace-nowrap' : ''
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {note ||
          'Prix de départ TTC à titre indicatif — le devis final dépend de l’état de l’appareil. Devis gratuit en boutique.'}
      </p>
    </div>
  </div>
);

/* FAQ en accordéon — même rendu que la FAQ de la home */
export const ServiceFAQ = ({ faq, title = 'Questions fréquentes' }) => (
  <section className="container-custom py-10 lg:py-12">
    <div className="max-w-3xl">
      <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{title}</h2>
      <Accordion type="single" collapsible className="space-y-3">
        {faq.map((item, idx) => (
          <AccordionItem
            key={idx}
            value={`faq-${idx}`}
            className="group review-glass-card !rounded-xl border-0 px-4 overflow-hidden data-[state=open]:ring-1 data-[state=open]:ring-blue-300/60 transition-all duration-300"
            data-testid={`service-faq-item-${idx}`}
          >
            <AccordionTrigger className="text-left font-medium text-slate-800 hover:text-blue-600 py-4 text-[15px] [&[data-state=open]]:text-blue-700 transition-colors gap-3">
              <span className="flex items-center gap-3 min-w-0">
                <span className="flex-shrink-0 w-6 h-6 rounded-md bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-600 text-[11px] font-bold group-data-[state=open]:from-blue-500 group-data-[state=open]:to-indigo-500 group-data-[state=open]:text-white transition-all">
                  {idx + 1}
                </span>
                <span className="leading-snug">{item.q}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-slate-600 pb-4 leading-relaxed pl-9 text-[14px]">
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

/* Maillage interne — cartes "Voir aussi" */
export const RelatedServices = ({ title = 'Voir aussi', links }) => {
  const navigate = useNavigate();
  return (
    <section className="container-custom py-10 lg:py-14">
      <div className="max-w-4xl">
        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{title}</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {links.map((link) => (
            <button
              key={link.label}
              onClick={() => {
                if (link.to.startsWith('/#')) {
                  try { sessionStorage.setItem('allorep:scrollTo', link.to.slice(1)); } catch {}
                  navigate('/');
                } else {
                  navigate(link.to);
                }
              }}
              className="group text-left p-5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-lg hover:border-blue-200 transition-all"
              data-testid={`related-${link.to}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                  {link.label}
                </span>
                <ArrowRight size={18} className="text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all flex-shrink-0" />
              </div>
              {link.desc && <p className="mt-1.5 text-sm text-slate-500">{link.desc}</p>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
